import { formatCurrency, formatNumber, useTranslation } from '@hbs-us/ui';

/**
 * Locale-aware money / percent formatters for the reports screen (I18N-05).
 *
 * These wrap the `@hbs-us/ui` `Intl`-based helpers with the ACTIVE i18n locale so
 * the same figures render with locale separators (EN `$1,234`, ES `1234 $`). The
 * legacy `ReportsPage` hand-rolled an en-US-locked number formatter — we must
 * NOT do that (it breaks ES). These wrappers always pass the active locale.
 * Money is whole-dollar (matching the legacy `maximumFractionDigits: 0`);
 * percent is one decimal place.
 */
export const HIDDEN_FIGURE = '—';

/** True when the server hid this figure from the reader. */
export const isHidden = (value: number | null | undefined): value is null => value === null;

export function useReportFormatters() {
  const { i18n } = useTranslation();
  const locale = i18n.language || 'en';

  // `null` is a figure the server hid from this reader (wages and profit without
  // financials.view). It prints as a dash, never as $0, which would be a wrong number.
  return {
    money: (value: number | null): string =>
      value === null
        ? HIDDEN_FIGURE
        : formatCurrency(value || 0, locale, 'USD', { maximumFractionDigits: 0 }),
    percent: (value: number | null): string =>
      value === null
        ? HIDDEN_FIGURE
        : `${formatNumber(value || 0, locale, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })}%`,
  };
}

export type ReportFormatters = ReturnType<typeof useReportFormatters>;
